import {
  ButtonHTMLAttributes,
  ComponentProps,
  useCallback,
  useMemo,
} from 'react';
import { Icon } from 'react-bulma-components';
import { FaArrowLeft } from 'react-icons/fa6';
import { useNavigate } from 'react-router-dom';
import Button from '../../ui/button/button';
import styles from './back-button.module.scss';

type ButtonProps = ComponentProps<typeof Button>;

export type BackButtonProps = ButtonProps &
  ButtonHTMLAttributes<HTMLButtonElement>;

export function BackButton({ onClick, ...props }: BackButtonProps) {
  const navigate = useNavigate();

  const buttonClassName = useMemo(() => {
    if (props.outlined) {
      return 'is-outlined is-primary-dark';
    }
    return `is-borderless is-transparent ${styles.hoverableBackground}`;
  }, [props.outlined]);

  const spanClassName = useMemo(() => {
    if (props.outlined) {
      return;
    }
    return 'is-underlined has-text-primary';
  }, [props.outlined]);

  const iconClassName = useMemo(() => {
    const className = 'is-justify-content-flex-start is-marginless';
    if (props.outlined) {
      return className;
    }
    return `has-text-primary ${className} ${styles.hoverableColor}`;
  }, [props.outlined]);

  const canNavigateBack = useMemo(() => {
    return window.history.length > 1;
  }, []);

  const navigateBack = useCallback(() => navigate(-1), [navigate]);

  // If no back navigation is available, the button should not be rendered
  if (!canNavigateBack) {
    return null;
  }

  return (
    <Button
      className={buttonClassName}
      onClick={onClick ?? navigateBack}
      {...props}
    >
      <Icon className={iconClassName}>
        <FaArrowLeft />
      </Icon>
      <span className={spanClassName}>Back</span>
    </Button>
  );
}

export default BackButton;
